import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  Method,
  JSX,
  Watch
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";
import { Locale } from "../../../common/locale";
import {
  ChEditCustomEvent,
  TabularGridMarkingChangedEvent,
  TabularGridColumnSortChangedEvent,
  TabularGridRowContextMenuEvent
} from "@genexus/chameleon-controls-library";
import { ObjectType } from "../../../components";

import { config } from "../../../common/config";

import { ContextMenuCallback } from "../../../components";

import { renderObjectTypeWithIcon } from "../common/utilities";

import { COLUMNS_IDS } from "../version-control/common/tabular-grid-render";
import sortTabularGridColumn, {
  type SortTabularGridColumnDataType
} from "../../../common/chameleon/tabular-grid-column-sort";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tabular-grid",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar",
  "components/tooltip"
];

const COMBO_BOX_ALL_VALUE = "all";

const DETAIL_LABEL_CLASSES = "detail__label body-semi-bold-s";

const ADD_ICON = getIconPath({
  category: "system",
  name: "add",
  colorType: "on-surface"
});
const EDITED_ICON = getIconPath({
  category: "system",
  name: "edit",
  colorType: "on-surface"
});
const FILTER_ICON = getIconPath({
  category: "window-tools",
  name: "filter",
  colorType: "on-surface"
});
const SEARCH_ICON = getIconPath({
  category: "system",
  name: "search",
  colorType: "on-surface"
});
const SUBSTRACT_ICON = getIconPath({
  category: "system",
  name: "substract",
  colorType: "on-surface"
});
const WARNING_ICON = getIconPath({
  category: "system",
  name: "warning",
  colorType: "warning"
});
const ERROR_ICON = getIconPath({
  category: "system",
  name: "error",
  colorType: "error"
});

@Component({
  tag: "gx-ide-team-dev-bring-changes",
  styleUrl: "bring-changes.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/team-dev-bring-changes"]
})
export class GxIdeTeamDevBringChanges {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  #commitLabel: string;
  #comboBoxStatesModel: { caption: string; value: ObjectStateForFilter }[];
  #checkedObjectsIdsArray: string[] = [];

  @Element() el: HTMLGxIdeTeamDevBringChangesElement;

  private tabularGridEl!: HTMLChTabularGridElement;

  /**
   * An array that holds the id's of the objects that have been merged already.
   */
  @State() alreadyMergedObjectsIdsArray: string[] = [];

  /**
   * It hols a boolean indicating if at least one object from the tabular-grid is checked, for merging.
   */
  @State() atLeastOneObjectIsChecked: boolean = false;

  /**
   * True while commitObjects hasn't been loaded or is empty. Used to display a loader.
   */
  @State() loading: boolean = true;

  /**
   * It hols a boolean indicating if at least one object from the tabular-grid is checked, for merging.
   */
  @State() objectsFiltered: CommitObject[] = [];

  /**
   * The current search filter value
   */
  @State() searchValue: string = "";

  /**
   * The current state filter value
   */
  @State() stateFilterValue: ObjectStateForFilter = COMBO_BOX_ALL_VALUE;

  /**
   * A set that stores the marked state of each object cell. This is necessary because filtering the grid * causes marked cells to lose their state.
   */
  @State() markedObjectsSet: Set<string> = new Set<string>();

  /**
   * A map that holds the current commit objects states ("pending" | "warning" | "error" | "merged" | "reverted")
   */
  @State() objectsStatesMap: Map<string, ObjectState> = new Map();
  @Watch("objectsStatesMap")
  objectsStatesMapChanged(newCommitObjectsStateMap: Map<string, ObjectState>) {
    const someObjectIsMerging = [...newCommitObjectsStateMap.entries()].find(
      ([, state]) => state === "merging" || state === "reverting"
    );

    if (!someObjectIsMerging) {
      this.merging = false;
    }
  }

  /**
   * A map that holds object messages, if any, for objects that could not be merged (these objects are in a "warning" or "error" state)
   */
  @State() commitObjectsMessagesMap: Map<string, string> = new Map();

  /**
   * Set to true when objects are being mered. This helps disabling controls until all objects are done.
   */
  @State() merging: boolean = false;

  /**
   * Detail about the commit
   */
  @Prop() readonly commitDetail: CommitDetail;

  /**
   * Commit information
   */
  @Prop() readonly commitInformation: CommitInformation[] = [];

  /**
   * Array of commit
   */
  @Prop() readonly commitObjects: CommitObject[] = [];
  @Watch("commitObjects")
  commitObjectsChanged(newCommitObjects: CommitObject[]) {
    this.loading = true;
    if (newCommitObjects.length) {
      // This is the first and only objects asignment
      this.loading = false;

      // All commit objects begin as "pending to merge"
      newCommitObjects.forEach(commitObject => {
        this.objectsStatesMap.set(
          commitObject.id,
          this.operationType === "revert" ? "to-revert" : "to-merge"
        );
      });

      this.objectsFiltered = this.commitObjects;
    }
  }

  /**
   * Determines whether the filters are rendered or not.
   */
  @Prop() readonly hideFilters: boolean = false;

  /**
   * The type of operation to perform: "merge" or "revert".
   */
  @Prop() readonly operationType: OperationType = "merge";

  /**
   * The callback that is triggered when the user wants to merge more than one commit.
   */
  @Prop() readonly mergeObjectsCallback!: MergeObjectsCallback;

  /**
   * If true, the "Action" column will display a button instead of a pill, only when the action is
   */
  @Prop() readonly revertPillAsButton: boolean = false;

  /**
   * Invoked when user activates the context menu on th grid.
   */
  @Prop() readonly contextMenuCallback!: ContextMenuCallback;

  /**
   * Callback to be executed when the user wants to view all comments.
   */
  @Prop() readonly viewAllCommentsCallback!: () => Promise<void>;

  /**
   * Callback to be executed when the user wants to edit the range.
   */
  @Prop() readonly editRangeCallback!: () => Promise<void>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#commitLabel =
      this.commitInformation.length > 1
        ? this.#componentLocale.commmits
        : this.#componentLocale.commmit;

    // Initiate "states" combo box filter
    this.#comboBoxStatesModel = [
      {
        caption: this.#componentLocale.states.all,
        value: COMBO_BOX_ALL_VALUE
      },
      {
        caption:
          this.operationType === "revert"
            ? this.#componentLocale.states["to-revert"]
            : this.#componentLocale.states["to-merge"],
        value: this.operationType === "revert" ? "to-revert" : "to-merge"
      },
      {
        caption: this.#componentLocale.states.warning,
        value: "warning"
      },
      {
        caption: this.#componentLocale.states.error,
        value: "error"
      },
      {
        caption:
          this.operationType === "revert"
            ? this.#componentLocale.states.reverted
            : this.#componentLocale.states.merged,
        value: this.operationType === "revert" ? "reverted" : "merged"
      }
    ];

    if (this.commitObjects?.length) {
      // if commitObjects already set...
      this.commitObjectsChanged(this.commitObjects);
    }
  }

  #formatComments = (): JSX.Element => {
    if (this.commitInformation.length === 1) {
      return <span>{this.commitInformation[0].comment}</span>;
    }
    const firstComment =
      this.commitInformation[this.commitInformation.length - 1].comment;
    return <span>{`${this.#componentLocale.latest}: ${firstComment}...`}</span>;
  };

  #mergeCheckedClickedHandler = async () => {
    this.merging = true;
    const newobjectsStatesMap = new Map(this.objectsStatesMap);
    this.#checkedObjectsIdsArray.forEach(checkedObjectId => {
      newobjectsStatesMap.set(
        checkedObjectId,
        this.operationType === "revert" ? "reverting" : "merging"
      );
    });

    this.objectsStatesMap = newobjectsStatesMap;
    await this.mergeObjectsCallback(this.#checkedObjectsIdsArray);
  };

  #mergeSingleObject: (objectId: string) => () => void =
    objectId => async () => {
      this.merging = true;
      const newobjectsStatesMap = new Map(this.objectsStatesMap);
      newobjectsStatesMap.set(
        objectId,
        this.operationType === "revert" ? "reverting" : "merging"
      );
      this.objectsStatesMap = newobjectsStatesMap;
      await this.mergeObjectsCallback([objectId]);
    };

  #renderActionCellContent = (
    actionTaken: ObjectActionTaken
  ): JSX.Element[] => {
    let icon: string;
    let caption: string;
    let iconColor: string;

    if (actionTaken === "Deleted") {
      icon = SUBSTRACT_ICON;
      caption = this.#componentLocale.objects.actions.delete;
      iconColor = "--color-icon-error-default";
    } else if (actionTaken === "Inserted") {
      icon = ADD_ICON;
      caption = this.#componentLocale.objects.actions.insert;
      iconColor = "--color-icon-success-default";
    } else {
      // modified
      icon = EDITED_ICON;
      caption = this.#componentLocale.objects.actions.modify;
      iconColor = "--color-icon-warning-default";
    }

    return (
      <span class="custom-icon-text-wrapper">
        <ch-image
          type="mask"
          class="icon-s"
          src={icon}
          style={{ backgroundColor: `var(${iconColor})` }}
        ></ch-image>
        {`${caption}`}
      </span>
    );
  };

  #renderCommitsNumbers = (onlyNumbers: boolean = false): string => {
    if (this.commitInformation?.length > 1) {
      // more than one commit
      return onlyNumbers
        ? `${this.commitInformation[0].id} ... ${
            this.commitInformation[this.commitInformation.length - 1].id
          }`
        : `${this.#commitLabel} ${this.commitInformation[0].id} ... ${
            this.commitInformation[this.commitInformation.length - 1].id
          }`;
    } else if (this.commitInformation?.length === 1) {
      // just one commit
      return onlyNumbers
        ? this.commitInformation[0].id
        : `${this.#commitLabel} ${this.commitInformation[0].id}`;
    } else {
      // Probably commits have not been loaded yet (loading state)
      return onlyNumbers
        ? `${this.#componentLocale.loading}`
        : `${this.#commitLabel} ${this.#componentLocale.loading}`;
    }
  };

  #renderFormatedDate = (date: Date): string => {
    const dateObject = new Date(date);
    const lang = navigator.languages?.[0] || navigator.language;

    return `${dateObject.toLocaleDateString(
      lang
    )} ${dateObject.toLocaleTimeString(lang, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    })}`;
  };

  #renderObjectStateWithIcon = (
    objectType: ObjectState,
    objectId: string
  ): JSX.Element[] => {
    if (objectType === "merging" || objectType === "reverting") {
      return (
        <span class="merging-spinner-caption">
          {this.operationType === "revert"
            ? this.#componentLocale.states.reverting
            : this.#componentLocale.states.merging}
        </span>
      );
    }
    const classes = `pill pill--${objectType} body-semi-bold-s`;
    if (
      this.revertPillAsButton &&
      (objectType === "to-merge" ||
        objectType === "to-merge-marked" ||
        objectType === "to-revert")
    ) {
      return (
        <button
          class={{ [classes]: true, "button-secondary": true }}
          onClick={this.#mergeSingleObject(objectId)}
        >
          {this.#componentLocale.statesForButton[objectType]}
        </button>
      );
    } else {
      return (
        <span class={classes}>{this.#componentLocale.states[objectType]}</span>
      );
    }
  };

  #renderObjectsRows = (): JSX.Element[] => {
    return this.objectsFiltered.map(commitObject => {
      const marked = this.markedObjectsSet.has(commitObject.id);

      const alreadyMerged = this.alreadyMergedObjectsIdsArray.find(
        checkedObjectId => checkedObjectId === commitObject.id
      );
      const errorOrWarningMessage = this.commitObjectsMessagesMap.get(
        commitObject.id
      );
      const state = this.objectsStatesMap.get(commitObject.id);

      let MESSAGE_ICON;
      if (state === "error") {
        MESSAGE_ICON = ERROR_ICON;
      } else if (state === "warning") {
        MESSAGE_ICON = WARNING_ICON;
      }

      return (
        <ch-tabular-grid-row
          class="tabular-grid-row"
          key={commitObject.id}
          rowid={commitObject.id}
          ref={(el: HTMLElement) =>
            marked && el?.setAttribute("marked", "true")
          }
        >
          <ch-tabular-grid-cell
            cell-type="rich"
            row-selector="true"
            class={{
              "tabular-grid-cell": true,
              "no-checkbox-custom-selector": alreadyMerged
            }}
            size="max-content"
          >
            {commitObject.name}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {renderObjectTypeWithIcon(commitObject.type)}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {commitObject.description}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {this.#renderActionCellContent(commitObject.actionTaken)}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell tabular-grid-cell--for-pill">
            {this.#renderObjectStateWithIcon(
              this.objectsStatesMap.get(commitObject.id),
              commitObject.id
            )}
          </ch-tabular-grid-cell>
          {errorOrWarningMessage && (
            <ch-tabular-grid-rowset class="tabular-grid-rowset">
              <ch-tabular-grid-rowset-empty
                class={{ "tabular-grid-rowset-empty": true, [state]: true }}
              >
                <p class="warning-error-description">
                  <ch-image
                    class="icon-s warning-error-icon"
                    src={MESSAGE_ICON}
                  ></ch-image>
                  {errorOrWarningMessage}
                </p>
              </ch-tabular-grid-rowset-empty>
            </ch-tabular-grid-rowset>
          )}
        </ch-tabular-grid-row>
      );
    });
  };

  #rowContextMenuHandler = async (
    ev: CustomEvent<TabularGridRowContextMenuEvent>
  ) => {
    ev.preventDefault();
    const selectedRowsIds = await this.tabularGridEl.getSelectedRows();

    await this.contextMenuCallback({
      selection: selectedRowsIds,
      clientX: ev.detail.clientX,
      clientY: ev.detail.clientY
    });
  };

  #rowMarkingChangedHandler = (
    event: CustomEvent<TabularGridMarkingChangedEvent>
  ) => {
    const newobjectsMarkedSet = new Set<string>();
    const newobjectsStatesMap = new Map(this.objectsStatesMap);
    const markedRowsIds = event.detail.rowsId;
    const checkedObjectsIgnoringAlreadyMerged = markedRowsIds.filter(
      markedObjectId =>
        !this.alreadyMergedObjectsIdsArray.includes(markedObjectId)
    );
    this.#checkedObjectsIdsArray = [...checkedObjectsIgnoringAlreadyMerged];

    // Update markedObjectsSet
    this.#checkedObjectsIdsArray.forEach(checkedObjectId => {
      newobjectsMarkedSet.add(checkedObjectId);
    });
    this.markedObjectsSet = newobjectsMarkedSet;

    this.atLeastOneObjectIsChecked = markedRowsIds.length > 0;

    newobjectsStatesMap.forEach(objectId => {
      const objectCurrentState = this.objectsStatesMap.get(objectId);
      if (
        objectCurrentState !== "to-merge" &&
        objectCurrentState !== "to-merge-marked"
      ) {
        return;
      }

      const markedRowIndex = markedRowsIds.findIndex(id => id === objectId);
      if (markedRowIndex !== -1) {
        newobjectsStatesMap.set(objectId, "to-merge-marked");
        markedRowsIds.splice(markedRowIndex, 1);
        markedRowsIds;
      } else {
        newobjectsStatesMap.set(objectId, "to-merge");
      }
    });

    this.objectsStatesMap = newobjectsStatesMap;
  };

  #searchInputHandler = (event: ChEditCustomEvent<string> | InputEvent) => {
    this.searchValue = (event.detail as string).toLowerCase();
    this.#updateFilteredObjects();
  };

  #stateChangedHandler = (event: CustomEvent<string> | InputEvent) => {
    this.stateFilterValue = event.detail as ObjectStateForFilter;
    this.#updateFilteredObjects();
  };

  #updateFilteredObjects = () => {
    let filteredObjects = [...this.commitObjects];

    // filter by state (ch-combo-box-render) ...
    if (this.stateFilterValue !== "all") {
      filteredObjects = filteredObjects.filter(object => {
        const objectState = this.objectsStatesMap.get(object.id);
        return objectState === this.stateFilterValue;
      });
    }

    // and filter by value (ch-edit) as well
    filteredObjects = filteredObjects.filter(object => {
      const includesName = object.name.toLowerCase().includes(this.searchValue);

      const includesType = object.type.name
        .toLowerCase()
        .includes(this.searchValue);

      const includesDescription = object.description
        .toLowerCase()
        .includes(this.searchValue);

      if (includesName || includesType || includesDescription) {
        return true;
      }
      return false;
    });

    this.objectsFiltered = filteredObjects;
  };

  /**
   * It allows the host updating an object state, by providing the object id.
   * @param objectId The ID of the object to update.
   * @param newState The new state to set on the object.
   * @param message Optional message explaining the change.
   *
   * @example
   * bringChanges.updateObjectState('obj-24', "merging", null);
   * bringChanges.updateObjectState('obj-27', "error", "The server did not respond.");
   */
  @Method()
  async updateObjectState(
    objectId: string,
    newState: ObjectState,
    message: string
  ) {
    // update object state
    const newobjectsStatesMap = new Map(this.objectsStatesMap);
    newobjectsStatesMap.set(objectId, newState);
    this.objectsStatesMap = newobjectsStatesMap;

    // add message if state is "warning" or "error"
    if (message?.length > 0) {
      this.commitObjectsMessagesMap.set(objectId, message);
    }

    if (newState === "merged" || newState === "reverted") {
      // remove from checked
      this.#checkedObjectsIdsArray = this.#checkedObjectsIdsArray.filter(
        checkedObject => checkedObject !== objectId
      );

      // add to "already merged". This will hide the checkbox
      this.alreadyMergedObjectsIdsArray.push(objectId);

      // unmark checkbox:
      this.tabularGridEl.markRow(objectId, false);

      // remove message if any
      this.commitObjectsMessagesMap.delete(objectId);
    }
  }

  #sortTabularGridColumnCallback = (
    e: CustomEvent<TabularGridColumnSortChangedEvent>
  ) => {
    const columnId = e.detail.columnId;

    let objectProperty;
    let dateType: SortTabularGridColumnDataType = "string";
    if (columnId === COLUMNS_IDS.type) {
      objectProperty = "name"; // the type name
      dateType = "object";
    }

    this.objectsFiltered = [
      ...sortTabularGridColumn(
        e,
        this.objectsFiltered,
        dateType,
        objectProperty
      )
    ];
  };

  #viewAllCommentsHandler = async () => {
    await this.viewAllCommentsCallback();
  };

  #editRangeHandler = async () => {
    await this.editRangeCallback();
  };

  render() {
    const enableRichRowSelector =
      this.alreadyMergedObjectsIdsArray.length !== this.commitObjects.length;

    const disableControls = this.merging || this.loading;
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="section">
          <header class="header spacing-body-inline spacing-body-block-start">
            <div
              // header details
              class="header__detail"
            >
              <p class="detail__title subtitle-semi-bold-s">
                {this.operationType === "revert"
                  ? this.#componentLocale.commitDetail.revertChangesDetail
                  : this.#componentLocale.commitDetail.bringChangesDetail}
              </p>
              {/* <p class="detail__commit-name subtitle-semi-bold-xs">
                {this.#renderCommitsNumbers()}
              </p> */}
              <div
                // genexus server
                class="detail__genexus-server field field-block"
              >
                <span class={DETAIL_LABEL_CLASSES}>
                  {this.#componentLocale.commitDetail.genexusServer}
                </span>
                <a
                  class="detail__link body-regular-s"
                  href={this.commitDetail.genexusServer}
                  target="_blank"
                >
                  {this.commitDetail.genexusServer}
                </a>
              </div>

              <div
                // knowledge base
                class="detail__knowledge-base field field-block"
              >
                <span class={DETAIL_LABEL_CLASSES}>
                  {this.#componentLocale.commitDetail.knowledgeBase}
                </span>
                <p class="body-regular-s">{this.commitDetail.knowledgeBase}</p>
              </div>

              <div
                // current version
                class="detail__current-version field field-block"
              >
                <span class={DETAIL_LABEL_CLASSES}>
                  {this.#componentLocale.commitDetail.currentVersion}
                </span>
                <a class="body-regular-s">{this.commitDetail.currentVersion}</a>
              </div>
              {(this.operationType === "merge" ||
                this.operationType === "merge-all") && (
                <div
                  // merge from version
                  class="detail__merge-from-version field field-block"
                >
                  <span class={DETAIL_LABEL_CLASSES}>
                    {this.#componentLocale.commitDetail.mergeFromVersion}
                  </span>
                  <p class="body-regular-s">
                    {this.commitDetail.mergeFromVersion}
                  </p>
                </div>
              )}
            </div>
            <div
              // header information
              class="header__information"
            >
              <p class="information__title subtitle-semi-bold-xs">
                {this.#componentLocale.commitInformation.title}
              </p>
              <div
                // header information (col-1)
                class="information__col-1"
              >
                <div
                  // IDs
                  class="field field-block"
                >
                  <span class={DETAIL_LABEL_CLASSES}>
                    {this.commitInformation.length === 1
                      ? this.#componentLocale.commitInformation.id
                      : this.#componentLocale.commitInformation.ids}
                  </span>
                  <p class="body-regular-s">
                    {this.#renderCommitsNumbers(true)}
                  </p>
                </div>
                {this.commitInformation.length === 1 ? (
                  <div class="field field-block">
                    <span class={DETAIL_LABEL_CLASSES}>
                      {this.#componentLocale.commitInformation.date}
                    </span>
                    <p class="body-regular-s">
                      {this.#renderFormatedDate(this.commitInformation[0].date)}
                    </p>
                  </div>
                ) : (
                  [
                    <div class="field field-block">
                      <span class={DETAIL_LABEL_CLASSES}>
                        {this.#componentLocale.commitInformation.from}
                      </span>
                      <p class="body-regular-s">
                        {this.#renderFormatedDate(
                          this.commitInformation[0].date
                        )}
                      </p>
                    </div>,
                    <div class="field field-block">
                      <span class={DETAIL_LABEL_CLASSES}>
                        {this.#componentLocale.commitInformation.to}
                      </span>
                      <p class="body-regular-s">
                        {this.#renderFormatedDate(
                          this.commitInformation[
                            this.commitInformation.length - 1
                          ].date
                        )}
                      </p>
                    </div>,
                    this.operationType === "merge-all" && (
                      <button
                        class="button-tertiary button-icon-and-text body-semi-bold-s edit-range-button"
                        type="button"
                        onClick={this.#editRangeHandler}
                      >
                        <ch-image
                          class="icon-m"
                          src={EDITED_ICON}
                          type="mask"
                        />
                        {this.#componentLocale.buttons.editRange}
                      </button>
                    )
                  ]
                )}
              </div>
            </div>
            <div class="header__comments">
              <div class="layout layout--cols-2-1">
                <div class="layout__panel">
                  <div class="field field-block">
                    <label
                      class={DETAIL_LABEL_CLASSES}
                      htmlFor="commit-comments"
                    >
                      {this.commitInformation.length > 1
                        ? `${this.#componentLocale.commitInformation.comments} (${this.commitInformation.length})`
                        : this.#componentLocale.commitInformation.comment}
                    </label>
                    <p class="body-regular-s comments-container">
                      {this.#formatComments()}
                    </p>
                  </div>
                  <button
                    class="button-tertiary body-semi-bold-s"
                    type="button"
                    onClick={this.#viewAllCommentsHandler}
                  >
                    {this.commitInformation.length > 1
                      ? this.#componentLocale.buttons.viewAllComments.replace(
                          "{{commentsCount}}",
                          this.commitInformation.length.toString()
                        )
                      : this.#componentLocale.buttons.viewComment}
                  </button>
                </div>
              </div>
            </div>
          </header>
          <div
            class={{
              "field-group": true,
              "controls-container": true,
              "filter": !this.hideFilters,
              "spacing-body-inline": true
            }}
          >
            {!this.hideFilters && [
              <ch-edit
                class="input"
                disabled={disableControls}
                startImgSrc={SEARCH_ICON}
                placeholder={this.#componentLocale.filter.searchPlaceholder}
                type="search"
                onInput={this.#searchInputHandler}
              ></ch-edit>,
              <ch-combo-box-render
                class="combo-box"
                disabled={disableControls}
                model={this.#comboBoxStatesModel}
                value={this.stateFilterValue}
                onInput={this.#stateChangedHandler}
                placeholder={this.#componentLocale.filter.statePlaceholder}
              ></ch-combo-box-render>
            ]}

            <button
              class="button-primary button-merge-checked"
              disabled={!this.atLeastOneObjectIsChecked || disableControls}
              onClick={this.#mergeCheckedClickedHandler}
            >
              {this.operationType === "revert"
                ? this.#componentLocale.filter.revertSelected
                : this.#componentLocale.filter.mergeSelected}
            </button>
          </div>
          <div class="main spacing-body">
            {this.commitObjects.length ? (
              <ch-tabular-grid
                class={{
                  "tabular-grid": true,
                  "objects-tabular-grid": true,
                  "disabled-custom-selector": this.merging,
                  "empty-result": this.objectsFiltered.length === 0
                }}
                keyboardNavigationMode="focus"
                rowSelectionMode="multiple"
                onRowMarkingChanged={this.#rowMarkingChangedHandler}
                onRowContextMenu={this.#rowContextMenuHandler}
                ref={(el: HTMLChTabularGridElement) =>
                  (this.tabularGridEl = el as HTMLChTabularGridElement)
                }
              >
                <ch-tabular-grid-columnset class="tabular-grid-column-set">
                  <ch-tabular-grid-column
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.name}
                    columnName={this.#componentLocale.objects.name}
                    columnType="rich"
                    richRowSelector={enableRichRowSelector}
                    richRowSelectorMode="mark"
                    settingable={false}
                    sortable={true}
                    onColumnSortChanged={this.#sortTabularGridColumnCallback}
                    size="max-content"
                  />
                  <ch-tabular-grid-column
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.type}
                    columnName={this.#componentLocale.objects.type}
                    settingable={false}
                    sortable={true}
                    onColumnSortChanged={this.#sortTabularGridColumnCallback}
                    size="max-content"
                  />
                  <ch-tabular-grid-column
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.description}
                    columnName={this.#componentLocale.objects.description}
                    settingable={false}
                    sortable={true}
                    onColumnSortChanged={this.#sortTabularGridColumnCallback}
                    size={config.tabularGrid.colSize.description}
                  />
                  <ch-tabular-grid-column
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.action}
                    columnName={this.#componentLocale.objects.action}
                    settingable={false}
                    sortable={false}
                    size="max-content"
                  />
                  <ch-tabular-grid-column
                    // size of this column should the enough for the largest pill, and no more.
                    // this prevents layout shifts when pills state changes.
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.status}
                    columnName={this.#componentLocale.objects.status}
                    settingable={false}
                    sortable={false}
                    size="100px"
                  />
                </ch-tabular-grid-columnset>
                {this.#renderObjectsRows()}

                <ch-tabular-grid-rowset class="tabular-grid-rowset">
                  {this.objectsFiltered.length === 0 && (
                    <ch-tabular-grid-rowset-empty>
                      <gx-ide-empty-state
                        class="empty-state"
                        isAnimated
                        stateIconSrc={FILTER_ICON}
                        stateTitle="No object matched your filter"
                      ></gx-ide-empty-state>
                    </ch-tabular-grid-rowset-empty>
                  )}
                </ch-tabular-grid-rowset>
              </ch-tabular-grid>
            ) : (
              <gx-ide-loader
                cancelLabel={this.#componentLocale.loader.cancelLabel}
                loaderTitle={this.#componentLocale.loader.title}
                description={this.#componentLocale.loader.description}
                show
                class="loader"
              ></gx-ide-loader>
            )}
          </div>
        </section>
      </Host>
    );
  }
}

export type OperationType = "merge" | "revert" | "merge-all";

export type CommitDetail = {
  genexusServer: string;
  knowledgeBase: string;
  currentVersion: string;
  mergeFromVersion: string;
};

export type CommitInformation = {
  id: string;
  comment: string;
  date: Date;
};

export type CommitObject = {
  id: string;
  date: Date;
  name: string;
  type: ObjectType;
  description: string;
  actionTaken: ObjectActionTaken;
};

export type ObjectActionTaken =
  | "Inserted"
  | "Deleted"
  | "Modified"
  | "Unchanged"
  | "Unknown";

export type ObjectState =
  | "to-merge"
  | "to-merge-marked"
  | "to-revert"
  | "merging"
  | "pending"
  | "warning"
  | "error"
  | "merged"
  | "reverted"
  | "reverting";

type ObjectStateForFilter =
  | Extract<
      ObjectState,
      "to-merge" | "to-revert" | "warning" | "error" | "merged" | "reverted"
    >
  | "all";

export type MergeObjectsCallback = (objectsIds: string[]) => Promise<void>;
